// src/config/googleDriveService.js
import { getAccessToken } from "./authContext";

const FOLDER_ID =
  process.env.REACT_APP_GOOGLE_DRIVE_FOLDER_ID ||
  "1HdZCvh9MmRBOHUdlHowMkqhqXlMwtZk-";

/**
 * Upload file ke Google Drive menggunakan OAuth Token
 */
export const uploadToGoogleDrive = async (file, fileName) => {
  try {
    const accessToken = getAccessToken();

    if (!accessToken) {
      throw new Error("Silakan login terlebih dahulu");
    }

    if (!file) {
      throw new Error("File tidak ditemukan");
    }

    const metadata = {
      name: fileName || file.name,
      parents: [FOLDER_ID],
      description: `Uploaded from app at ${new Date().toISOString()}`,
    };

    const formData = new FormData();
    formData.append(
      "metadata",
      new Blob([JSON.stringify(metadata)], { type: "application/json" }),
    );
    formData.append("file", file);

    const response = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      },
    );

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 401) {
        throw new Error("SESSION_EXPIRED");
      }

      throw new Error(errorData.error?.message || "Upload failed");
    }

    const data = await response.json();

    // Make file public (agar bisa diakses tanpa login)
    try {
      await fetch(
        `https://www.googleapis.com/drive/v3/files/${data.id}/permissions`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role: "reader",
            type: "anyone",
          }),
        },
      );
    } catch (permError) {
      console.warn("Could not make file public:", permError);
    }

    return {
      id: data.id,
      name: data.name,
      viewLink: `https://drive.google.com/file/d/${data.id}/view`,
      webViewLink: `https://drive.google.com/file/d/${data.id}/view`,
      webContentLink: `https://drive.google.com/uc?export=download&id=${data.id}`,
      thumbnailLink: `https://drive.google.com/thumbnail?id=${data.id}&sz=w400`,
      createdTime: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Upload error:", error);
    throw error;
  }
};

/**
 * Get files from Google Drive folder
 */
export const getFilesFromFolder = async () => {
  try {
    const accessToken = getAccessToken();

    if (!accessToken) {
      console.warn("No access token available");
      return [];
    }

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q='${FOLDER_ID}'+in+parents and trashed=false&fields=files(id,name,webViewLink,createdTime,size,mimeType)&orderBy=createdTime desc`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 401) {
        throw new Error("SESSION_EXPIRED");
      }

      throw new Error(errorData.error?.message || "Failed to fetch files");
    }

    const data = await response.json();
    return (data.files || []).map((file) => ({
      id: file.id,
      name: file.name,
      webViewLink:
        file.webViewLink || `https://drive.google.com/file/d/${file.id}/view`,
      webContentLink: `https://drive.google.com/uc?export=download&id=${file.id}`,
      thumbnailLink: `https://drive.google.com/thumbnail?id=${file.id}&sz=w400`,
      createdTime: file.createdTime || new Date().toISOString(),
      size: file.size,
      mimeType: file.mimeType,
    }));
  } catch (error) {
    console.error("Error fetching files:", error);
    throw error;
  }
};

/**
 * Delete file from Google Drive
 */
export const deleteFileFromDrive = async (fileId) => {
  try {
    const accessToken = getAccessToken();

    if (!accessToken) {
      throw new Error("Silakan login terlebih dahulu");
    }

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 401) {
        throw new Error("SESSION_EXPIRED");
      }

      throw new Error(errorData.error?.message || "Failed to delete file");
    }

    return true;
  } catch (error) {
    console.error("Delete error:", error);
    throw error;
  }
};

/**
 * Get file info
 */
export const getFileInfo = async (fileId) => {
  try {
    const accessToken = getAccessToken();

    if (!accessToken) {
      throw new Error("Silakan login terlebih dahulu");
    }

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,webViewLink,createdTime,description,size,mimeType`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 401) {
        throw new Error("SESSION_EXPIRED");
      }

      throw new Error(errorData.error?.message || "Failed to get file info");
    }

    const data = await response.json();
    return {
      id: data.id,
      name: data.name,
      webViewLink:
        data.webViewLink || `https://drive.google.com/file/d/${data.id}/view`,
      webContentLink: `https://drive.google.com/uc?export=download&id=${data.id}`,
      thumbnailLink: `https://drive.google.com/thumbnail?id=${data.id}&sz=w400`,
      createdTime: data.createdTime || new Date().toISOString(),
      description: data.description || "",
      size: data.size,
      mimeType: data.mimeType,
    };
  } catch (error) {
    console.error("Error getting file info:", error);
    throw error;
  }
};

/**
 * Search files
 */
export const searchFiles = async (keyword) => {
  try {
    const accessToken = getAccessToken();

    if (!accessToken) {
      return [];
    }

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q='${FOLDER_ID}'+in+parents and name contains '${encodeURIComponent(keyword)}' and trashed=false&fields=files(id,name,webViewLink,createdTime)&orderBy=createdTime desc`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "Search failed");
    }

    const data = await response.json();
    return (data.files || []).map((file) => ({
      id: file.id,
      name: file.name,
      webViewLink: `https://drive.google.com/file/d/${file.id}/view`,
      webContentLink: `https://drive.google.com/uc?export=download&id=${file.id}`,
      thumbnailLink: `https://drive.google.com/thumbnail?id=${file.id}&sz=w400`,
      createdTime: file.createdTime || new Date().toISOString(),
    }));
  } catch (error) {
    console.error("Search error:", error);
    return [];
  }
};

export default {
  uploadToGoogleDrive,
  getFilesFromFolder,
  deleteFileFromDrive,
  getFileInfo,
  searchFiles,
};
